import { useState, useEffect, use, useMemo } from 'react';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useManuscripts, DEFAULT_MANUSCRIPT_ID } from '@/context/manuscripts-context';
import { BUILTIN_MARK_STYLES } from '@/data/manuscript/manuscript-constants';
import { usePassageState } from '@/lib/hooks/use-passage-state';
import { usePassage } from '@/lib/hooks/use-passage';
import { ManuscriptService } from '@/data/manuscript/manuscript-service';
import useUser from '@/lib/hooks/use-user';
import { DBContext } from '@/lib/context';
import { BookOpen, FileText, Trash2 } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import notebookPenIcon from '@/assets/icons/notebook-pen.png';
import notebookPenIconDark from '@/assets/icons/notebook-pen-dark.png';

// Custom icon using the exact notebook-pen image
const CustomNotebookPen = ({ className }: { className?: string }) => {
    const isDark = useDarkMode();

    return (
        <img
            src={isDark ? notebookPenIconDark : notebookPenIcon}
            alt="Notebook with pen"
            className={className}
        />
    );
};

const COLORS = [
    '#6366f1', // Indigo
    '#10b981', // Emerald
    '#3b82f6', // Blue
    '#f59e0b', // Amber
    '#ef4444', // Red
    '#84cc16', // Lime
    '#737373', // Gray
];

interface ManuscriptEditDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    manuscriptId: string | null;
}

export function ManuscriptEditDialog({ open, onOpenChange, manuscriptId }: ManuscriptEditDialogProps) {
    const { manuscripts, createManuscript, updateManuscript, deleteManuscript } = useManuscripts();
    const { version, scrollState, passageId } = usePassageState();
    const { section: currentPassage } = usePassage();
    const { user } = useUser();
    const db = use(DBContext);

    const currentBook = useMemo(() =>
        currentPassage?.book || scrollState.book || null,
        [currentPassage?.book, scrollState.book]);

    const [name, setName] = useState('');
    const [color, setColor] = useState(COLORS[0]);
    const [scope, setScope] = useState<ManuscriptScope>('global');
    const [usageCounts, setUsageCounts] = useState<Record<string, number>>({});
    const [loading, setLoading] = useState(false);

    const isEditing = !!manuscriptId;
    const manuscript = useMemo(() => {
        const found = manuscripts.find(m => m.id === manuscriptId);
        if (found) return found;

        // Fallback for ghost default
        if (manuscriptId === DEFAULT_MANUSCRIPT_ID) {
            return {
                id: DEFAULT_MANUSCRIPT_ID,
                name: 'Personal Study',
                color: '#6366f1',
                scope: 'global' as ManuscriptScope,
                markStyles: BUILTIN_MARK_STYLES
            } as Manuscript;
        }

        return null;
    }, [manuscripts, manuscriptId]);

    const displayMarkStyles = useMemo(() => {
        if (manuscript?.markStyles && manuscript.markStyles.length > 0) {
            return manuscript.markStyles;
        }
        return BUILTIN_MARK_STYLES;
    }, [manuscript]);

    useEffect(() => {
        if (open) {
            if (manuscript) {
                setName(manuscript.name);
                setColor(manuscript.color);
                setScope(manuscript.scope);

                // Load usage counts
                if (user) {
                    ManuscriptService.getMarkStyleUsageCounts(db, user.uid, manuscript.id)
                        .then(setUsageCounts)
                        .catch(console.error);
                }
            } else {
                setName('');
                setColor(COLORS[Math.floor(Math.random() * COLORS.length)]);
                setScope('global');
                setUsageCounts({});
            }
        }
    }, [open, manuscript, user, db]);

    const handleSave = async () => {
        if (!name.trim()) return;

        setLoading(true);
        try {
            if (isEditing && manuscriptId) {
                const updates: any = { name, color, scope };

                // When changing scope, ensure we set the correct context values
                if (scope === 'book') {
                    updates.bookName = currentBook;
                    updates.passageIds = null; // Clear passage restrictions
                } else if (scope === 'passages') {
                    updates.passageIds = passageId ? [passageId] : (manuscript?.passageIds || []);
                    updates.bookName = null; // Clear book restrictions
                } else {
                    updates.bookName = null;
                    updates.passageIds = null;
                }

                await updateManuscript(manuscriptId, updates);
            } else {
                await createManuscript({
                    name,
                    color,
                    scope,
                    version,
                    bookName: scope === 'book' ? currentBook : null,
                    passageIds: scope === 'passages' && passageId ? [passageId] : null
                });
            }
            onOpenChange(false);
        } catch (error) {
            console.error('Failed to save manuscript:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!manuscriptId) return;
        if (!confirm('Are you sure you want to delete this manuscript? All annotations within it will be permanently lost.')) return;

        setLoading(true);
        try {
            await deleteManuscript(manuscriptId);
            onOpenChange(false);
        } catch (error) {
            console.error('Failed to delete manuscript:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[425px]" onOpenAutoFocus={(e) => e.preventDefault()}>
                <DialogHeader>
                    <DialogTitle>{isEditing ? 'Edit Manuscript' : 'New Manuscript'}</DialogTitle>
                    <DialogDescription className="text-xs text-muted-foreground pt-1.5 leading-normal italic">
                        A manuscript is like a distinct "physical" copy of your Bible. Use manuscripts to maintain separate workspaces for different contexts - like one for deep personal study and another for a specific teaching series.
                    </DialogDescription>
                </DialogHeader>

                <div className="grid gap-4 py-4">
                    <div className="grid gap-2">
                        <Label htmlFor="name">Name</Label>
                        <Input
                            id="name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g. Personal Study, Galatians Series, 2026"
                        />
                    </div>

                    <div className="grid gap-2">
                        <Label>Color</Label>
                        <div className="flex gap-2">
                            {COLORS.map((c) => (
                                <button
                                    key={c}
                                    className={`h-6 w-6 rounded-full border-2 transition-all ${color === c ? 'border-primary scale-110 shadow-sm' : 'border-transparent hover:border-muted'}`}
                                    style={{ backgroundColor: c }}
                                    onClick={() => setColor(c)}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="grid gap-2">
                        <Label>Scope</Label>
                        <RadioGroup value={scope} onValueChange={(v) => setScope(v as ManuscriptScope)} className="grid grid-cols-1 gap-2">
                            <Label
                                htmlFor="scope-global"
                                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${scope === 'global' ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'}`}
                            >
                                <RadioGroupItem value="global" id="scope-global" className="sr-only" />
                                <CustomNotebookPen className="h-4 w-4 text-muted-foreground" />
                                <div className="flex-1">
                                    <div className="font-medium text-sm">Global</div>
                                    <p className="text-xs text-muted-foreground">Visible everywhere in this translation</p>
                                </div>
                            </Label>

                            <Label
                                htmlFor="scope-book"
                                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${scope === 'book' ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'}`}
                            >
                                <RadioGroupItem value="book" id="scope-book" className="sr-only" />
                                <BookOpen className="h-4 w-4 text-muted-foreground" />
                                <div className="flex-1">
                                    <div className="font-medium text-sm">Book: {currentBook || 'current'}</div>
                                    <p className="text-xs text-muted-foreground">Only visible within this Bible book</p>
                                </div>
                            </Label>

                            <Label
                                htmlFor="scope-passages"
                                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${scope === 'passages' ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'}`}
                            >
                                <RadioGroupItem value="passages" id="scope-passages" className="sr-only" />
                                <FileText className="h-4 w-4 text-muted-foreground" />
                                <div className="flex-1">
                                    <div className="font-medium text-sm">Current Passage</div>
                                    <p className="text-xs text-muted-foreground">Only visible within this specific passage</p>
                                </div>
                            </Label>
                        </RadioGroup>

                        {isEditing && manuscript && scope !== 'global' && manuscript.scope === 'global' && (
                            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 flex items-start gap-2 mt-1">
                                <div className="text-amber-500 mt-0.5">⚠️</div>
                                <p className="text-xs text-amber-700 leading-normal">
                                    Changing to a smaller scope will hide any marks you've already made in other books or passages. They won't be deleted, but they'll only reappear if you set the scope back to Global.
                                </p>
                            </div>
                        )}
                    </div>

                    {displayMarkStyles.length > 0 && (
                        <>
                            <Separator className="my-2" />
                            <div className="grid gap-2">
                                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                    {isEditing ? 'Mark Styles Usage' : 'Included Mark Styles'}
                                </Label>
                                <div className="grid gap-2 max-h-[150px] overflow-y-auto pr-2">
                                    {displayMarkStyles.map(style => (
                                        <div key={style.id} className="flex items-center justify-between text-sm py-1 border-b border-muted/30 last:border-0">
                                            <div className="flex items-center gap-2 overflow-hidden">
                                                <div className="h-2 w-2 rounded-full flex-shrink-0" style={{ backgroundColor: style.color }} />
                                                <span className="truncate">{style.name}</span>
                                            </div>
                                            <span className="text-xs text-muted-foreground tabular-nums">
                                                {usageCounts[style.id] || 0} marks
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                </div>

                <DialogFooter className="flex items-center justify-between gap-2 sm:justify-between">
                    {isEditing && !!manuscript ? (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={handleDelete}
                            disabled={loading}
                        >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                        </Button>
                    ) : <div />}

                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
                            Cancel
                        </Button>
                        <Button onClick={handleSave} disabled={loading || !name.trim()}>
                            {isEditing ? 'Save Changes' : 'Create Manuscript'}
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
